/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import IPageModel from 'apmui/page';
import IDialogConfiguration, { DialogType, IDialogEntry } from './Model';

function equal(dialog: IDialogConfiguration, dialogClassOrId: DialogType | IDialogConfiguration) {
    if (typeof dialogClassOrId === 'string') {
        return dialog.id === dialogClassOrId;
    }
    // dialogClassOrId.resource may refer to some object with id and type members
    // and not necessarily to WebtopResourceModel instance
    if (dialog.id !== dialogClassOrId.id || !dialog.resource !== !dialogClassOrId.resource) {
        return false;
    }
    if (dialog.resource) {
        return (
            dialog.resource.id === dialogClassOrId.resource!.id &&
            dialog.resource.type === dialogClassOrId.resource!.type
        );
    }
    return true;
}

function get(page: IPageModel, dialogClassOrId: DialogType | IDialogConfiguration): IDialogConfiguration | null {
    for (const item of page.dialogs) {
        if (equal(item, dialogClassOrId)) {
            return item;
        }
    }
    return null;
}

/**
 * Provides result value for dialog if it was initiated with App.ask
 * @param dialog dialog configuration
 */
function resolve(dialog?: IDialogConfiguration, value?: any): void {
    if (dialog && dialog.callback) {
        dialog.callback.resolve(value);
    }
}

/**
 * Get Dialog field by Name
 * @param dialog dialog configuration
 * @param name   field name
 */
function getField(dialog: IDialogConfiguration, name: string) {
    if (dialog && dialog.entries) {
        for (const entry of dialog.entries) {
            if ('type' in entry && entry.name === name) {
                return entry;
            }
        }
    }
    return null;
}

function setFieldValue(dialog: IDialogConfiguration, name: string, value: string) {
    const field = getField(dialog, name);
    if (field) {
        field.value = value;
    }
}

/**
 * Get Dialog action by ActionType
 */
function getAction(dialog: IDialogConfiguration, type: string) {
    if (dialog.entries) {
        for (const entry of dialog.entries) {
            if ('id' in entry && entry.id === type) {
                return entry;
            }
        }
    }
    return null;
}

function insertBefore(dialog: IDialogConfiguration, entry: IDialogEntry, newEntry: IDialogEntry) {
    if (dialog.entries) {
        const idx = dialog.entries.indexOf(entry);
        dialog.entries.splice(idx > 0 ? idx : 0, 0, newEntry);
    }
}

export { equal, get, getAction, getField, insertBefore, resolve, setFieldValue };
